import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Observable, ReplaySubject, Subject, Subscription, throwError } from "rxjs";
import { filter, take } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import * as Gilde from "@bfx/gilde";

import { v4 as uuid } from "uuid";
import { MISC_STATIC_DATA } from "./CubiqBrokerClient.constants";
import { ConnectionStatus } from "../connection-status/model/ConnectionStatus";
import { ConnectionStatusState } from "../connection-status/model/ConnectionStatusState";
import { TokenState } from "./TokenState";
import { TRADABLE_ACCOUNTS, TRADABLE_CLIENTS } from "shared/tradable-services";

import { CubiqLogger } from "./CubiqLogger";
import { DoubleBufferQueue } from "./DoubleBufferQueue";

const CUBIQ_BROKER_BUILD_TOKEN = `{"class":"CubiqBrokerClient","version":"${environment.version}"}`;

const PROCESS_TICK_MS = 50;
const JITTER_FRACTION = 0.1; // ±5% around baseline
const DRIFT_WARN_MULTIPLIER = 4; // warn if > 4x expected tick interval
const DRIFT_WARN_COOLDOWN_MS = 10_000; // rate limit warnings

/** Default timeout for postServiceRequest promises (ms). */
const SERVICE_REQUEST_TIMEOUT_MS = 630_000;

export class CubiqBrokerClient {
  private singleton: object = null;
  private sessionId = uuid();
  private connectionSessionId: string | null = null;
  private proxyConnectionId: string | null = null;

  private get sessionCtx(): string {
    return `connectionSessionId=${this.connectionSessionId} proxyConnectionId=${this.proxyConnectionId}`;
  }

  /**
   * js types may be mangled arbitrarily by webpack even with terser preserving names
   *
   */
  private encodableType: Map<string, string> = new Map<string, string>();

  /**
   * Opposite of Keys handled during MISC_STATIC_DATA
   */
  private soloFixedKeys = new Set<string>([
    CubiqTS.ClientImpl.name,
    TRADABLE_CLIENTS,
    CubiqTS.AccountImpl.name,
    TRADABLE_ACCOUNTS,
    CubiqTS.CurrencyPairImpl.name,
    CubiqTS.CurrencyImpl.name,
    CubiqBrokerClient.name,
    MISC_STATIC_DATA,
  ]);

  private miscFixedKeys = new Map<string, string>([
    [CubiqTS.StaticDataProfileImpl.name, null],
    [CubiqTS.CurrencyPairGroupImpl.name, "currencyPairGroups"],
    [CubiqTS.BenchmarkFixingImpl.name, "benchmarkFixings"],
    [CubiqTS.BranchImpl.name, "branches"],
    [CubiqTS.SystemControlImpl.name, "offlineSystemControls"],
    [CubiqTS.TradingGroupImpl.name, "allTradingGroups"],
    [CubiqTS.NdfSourceImpl.name, "fixingSources"],
    [CubiqTS.SalesGroupImpl.name, "allSalesGroups"],
    [CubiqTS.ProfileSettingImpl.name, "profileSettings"],
    [CubiqTS.TradingChannelProfileImpl.name, "tradingChannelProfiles"],
    [CubiqTS.SystemSettingImpl.name, "systemSettings"],
    [CubiqTS.UserImpl.name, "users"],
    [CubiqTS.TimeZoneImpl.name, "timeZones"],
    [CubiqTS.ClientContactImpl.name, "clientContacts"],
    [CubiqTS.MarketTimeConstantImpl.name, "marketTimeConstants"],
    [CubiqTS.RateSourceImpl.name, "rateSources"],
    [CubiqTS.AlgoStrategyImpl.name, "algoStrategies"],
    [CubiqTS.AlgoHedgeConfigInfoImpl.name, "algoHedgeConfigInfos"],
    [CubiqTS.CustomFieldImpl.name, "customFieldDefinitions"],
    [CubiqTS.AutoHedgingStatusEventImpl.name, "autoHedgingStatusEvent"],
    [CubiqTS.PlatformProfileImpl.name, "platformProfiles"],
  ]);

  private fixedKeys = new Set<string>([
    ...this.soloFixedKeys.values(),
    ...this.miscFixedKeys.keys(),
  ]);

  private resolver = new CubiqTS.CubiqResolver();
  public authenticationSuccess = null;
  public authenticationFailure = null;
  private serviceLocation: string = null;
  private callbacks: Record<string, Array<(event: CubiqTS.ConcreteEncodable) => void>> =
    Object.create(null);

  private tokenResponse: CubiqTS.ServiceResponseImpl;
  private token: CubiqTS.TrustToken;
  private tokenId: string;
  private tokenSubject = new ReplaySubject<TokenState>(1);
  public token$ = this.tokenSubject.asObservable();

  protected port: MessagePort;

  private processingTimer: null | number | NodeJS.Timeout = null;
  private lastTick = 0;
  private messageBuffer = new DoubleBufferQueue<CubiqTS.ConcreteEncodable>("message");
  private brokerBuffer = new DoubleBufferQueue<CubiqTS.ConcreteEncodable>("broker");
  private processingEnabled = false;
  private processingGeneration = 0;
  private isProcessing = false;
  private lastDriftWarnAt = 0;

  private flushAfterStopGen: number | null = null;

  private reconnectionCount = 0;
  private _reconnectionDelay: number = CubiqBrokerClient.BASE_RECONNECT_MS;
  private reconnectionTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private visibilityListenerInstalled = false;

  private connectionStatusSubject: Subject<ConnectionStatus> = new ReplaySubject(1);
  private connectionStatus: ConnectionStatus = new ConnectionStatus(ConnectionStatusState.NONE);

  public connectionEvent$ = this.connectionStatusSubject.asObservable();

  private listenerSubjectMap: Map<string, Subject<any>> = new Map();
  private staticDataSubjectMap: Map<string, Subject<any>> = new Map();
  private _systemName: string = null;
  private _user: CubiqTS.UserImpl;
  private _gilde;

  /** For logging of all ui/server comms */
  private cubiqLogger: CubiqLogger;

  handler: (event: any) => void;

  private static readonly MAX_RECONNECT_BACKOFF_MS = 60_000;
  private static readonly BASE_RECONNECT_MS = 1_000;

  private refreshTokenSubscription: Subscription | null = null;

  private get serviceResponseSubject() {
    return this.getEncodableObservable<CubiqTS.ServiceResponseImpl>(
      CubiqTS.ServiceResponseImpl.name,
    );
  }

  public get reconnectionDelay(): number {
    return this._reconnectionDelay;
  }

  /**
   * Allows external configuration of the base reconnection delay.
   * Values below BASE_RECONNECT_MS are clamped up; non-finite values
   * are ignored so a bad config load cannot break reconnection.
   */
  public set reconnectionDelay(value: number) {
    if (!Number.isFinite(value)) {
      logger.out.warn(`${this.constructor.name}: ignoring non-finite reconnectionDelay: ${value}`);
      return;
    }
    this._reconnectionDelay = Math.max(value, CubiqBrokerClient.BASE_RECONNECT_MS);
  }

  constructor(sessionTimestamp: string) {
    logger.out.info(`${this.constructor.name} ${this.sessionId}: init`);

    this.cubiqLogger = new CubiqLogger();

    this.tokenSubject.next(TokenState.NONE);

    this.resolver.javaToJavaScriptType.forEach((jsType, javaType) => {
      // eslint-disable-next-line import/namespace
      const ctor = CubiqTS[jsType];
      if (ctor) {
        this.encodableType.set(ctor.name, javaType);
      } else {
        logger.out.warn(`CubiqBrokerClient: resolver maps ${javaType} to ${jsType} but CubiqTS.${jsType} is not exported`);
      }
    });

    this.registerEventChannelListener(CubiqTS.ServiceResponseImpl.name);
    this._gilde = new Gilde().setSecret(`${CUBIQ_BROKER_BUILD_TOKEN}_${sessionTimestamp}`);
  }

  public setSingleton(singleton: object) {
    if (this.hasSingleton()) {
      throw new Error("CubiqBrokerClient must be owned by a singleton");
    }
    this.singleton = singleton;
  }

  public getSingleton(): object {
    return this.singleton;
  }

  public hasSingleton(): boolean {
    return this.singleton !== null;
  }

  public getCubiqLogger(): CubiqLogger {
    return this.cubiqLogger;
  }

  public setTargetPort(target: MessagePort, port: MessagePort) {
    let buildToken = JSON.parse(CUBIQ_BROKER_BUILD_TOKEN);
    let data = {
      port: port,
      buildToken: buildToken,
      sig: this._gilde.create(buildToken),
    };

    target.postMessage(data, [port]);
  }

  public setSourcePort(port: MessagePort) {
    this.port = port;
    this.handler = (event) => {
      let data = event.data;

      if (typeof data === "object" && data !== null) {
        if (data.hasOwnProperty(`__cubiq_broker_message_type`)) {
          let type = data.__cubiq_broker_message_type;
          let payload = data.__cubiq_broker_message_payload;

          switch (type) {
            case "brokerMessage":
              this.processBrokerMessage(payload, data.__cubiq_broker_message_id);
              break;
            case "message":
              this.processSocketMessage(payload);
              break;
            case "onDisconnected":
              // this can occur on the login screen
              this.onDisconnected();
              break;
            case "onConnected":
              this.onConnected();
              break;
            case "onReconnected":
              this.onReconnected();
              break;
            case "tokenId":
              if (data.__cubiq_broker_message_payload != null) {
                this.tokenId = data.__cubiq_broker_message_payload;
                logger.out.info(
                  `${this.constructor.name} ${this.sessionId}: tokenId token pending update ${this.tokenId}`,
                );
                this.tokenSubject.next(TokenState.REQUESTABLE);
                this.requestToken();
              } else {
                logger.out.info(
                  `${this.constructor.name} ${this.sessionId}: tokenId token cleared`,
                );
                this.tokenId = null;
                this.tokenSubject.next(TokenState.NONE);
              }
              break;
            case "ackRefreshToken":
              if (this.tokenId === data.__cubiq_broker_message_payload) {
                logger.out.warn(
                  `${this.constructor.name} ${this.sessionId}: ackRefreshToken token refresh accepted ${this.tokenId} ${this.sessionCtx}`,
                );
                this.refreshToken();
              } else {
                logger.out.warn(
                  `${this.constructor.name} ${this.sessionId}: ackRefreshToken token refresh mismatch ${this.tokenId} ${this.sessionCtx}`,
                );
              }
              break;
            case "nackRefreshToken":
              {
                const payloadTokenId = data.__cubiq_broker_message_payload;
                const reason = data.__cubiq_broker_message_reason;

                logger.out.info(
                  `nackRefreshToken received (payload=${payloadTokenId}, current=${this.tokenId}, reason=${reason ?? "none"}) ${this.sessionCtx}`,
                );

                if (reason === "invalid_payload") {
                  logger.out.warn(
                    `nackRefreshToken invalid_payload -> requesting tokenId resync ${this.sessionCtx}`,
                  );
                  this.requestTokenId();
                  break;
                }
                if (payloadTokenId !== this.tokenId && reason === "mismatch") {
                  logger.out.warn(
                    `nackRefreshToken mismatch -> requesting tokenId resync ${this.sessionCtx}`,
                  );
                  this.requestTokenId();
                  break;
                }

                if (reason === "socket_not_open" || reason === "reload_pending") {
                  logger.out.error(
                    `nackRefreshToken terminal reason=${reason}. Clearing refresh timer and disconnecting. ${this.sessionCtx}`,
                  );
                  this.disconnect();
                  break;
                }
              }
              break;
            case "onPing": {
              this.proposeRefreshToken();
              break;
            }
            case "log":
              try {
                const info = JSON.parse(data.__cubiq_broker_message_payload);
                // level symbol does not make the jump.
                if (
                  info != null &&
                  typeof info === "object" &&
                  typeof info.level === "string" &&
                  typeof info.message === "string"
                ) {
                  logger.out[info.level](
                    `${this.constructor.name} ${this.sessionId}: CubiqBroker log message:\n${info.message}`,
                  );
                }
              } catch (e) {}
              break;
            default:
              //
              logger.out.warn(
                `${this.constructor.name} ${this.sessionId}: unknown message type ${data.__cubiq_broker_message_type}`,
              );
              break;
          }
        }
      }
    };

    this.port.addEventListener("message", this.handler);

    this.port.start();

    if (!this.visibilityListenerInstalled && typeof document?.addEventListener === "function") {
      this.visibilityListenerInstalled = true;
      document?.addEventListener?.("visibilitychange", () => {
        if (this.isIdle()) {
          // browser about to throttled us heavily flush outstanding and handle directly.
          this.stopProcessingLoop();
        } else {
          this.startProcessingLoop();
        }
      });
    }
  }

  /**
   *
   * @param typeName
   * @returns javascript type usable with new CubiqTS\[objectTypeName\]() and
   * comparable to ServiceResponse.serviceRequestType
   */
  getRuntimeObjectTypeName(typeName: string) {
    return this.resolver.resolveObject(this.encodableType.get(typeName));
  }
  /**
   *
   * @param typeName
   * @returns java type matching ConcreteEncodable.encodableType
   */
  getRuntimeEncodableType(typeName: string) {
    return this.encodableType.get(typeName);
  }

  /**
   *
   * @param typeName constructor name of CubiqTS ConcreteEncodable subclass
   * @returns
   */
  getEventListeners(typeName: string): any {
    return this.callbacks[this.getRuntimeEncodableType(typeName)];
  }

  /**
   * subjectListener _defaults_ to true if fnListener is undefined.
   * both fnListener & subject are permitted
   *
   * @param typeName constructor name of CubiqTS ConcreteEncodable subclass
   * @param fnListener _optional_ callback based listener
   * @param subjectListener add a subject based listener.
   */
  registerEventChannelListener(
    typeName: string,
    fnListener?: (
      event: CubiqTS.ConcreteEncodable,
    ) => void | [(event: CubiqTS.ConcreteEncodable) => void],
    subjectListener = fnListener == null,
  ) {
    typeName = this.encodableType.get(typeName);

    if (!this.callbacks[typeName]) {
      this.callbacks[typeName] = [];
    }

    if (subjectListener && this.listenerSubjectMap.has(typeName) == false) {
      const listenerSubject = new Subject();
      this.listenerSubjectMap.set(typeName, listenerSubject);
      this.callbacks[typeName].push((event: CubiqTS.ConcreteEncodable) => {
        listenerSubject.next(event);
      });
    }

    if (fnListener) {
      this.callbacks[typeName].push(fnListener);
    }
  }

  /**
   * completes subject based listeners then deletes all listeners
   *
   * @param typeName constructor name of CubiqTS ConcreteEncodable subclass
   */
  unregisterEventChannelListeners(typeName: string) {
    typeName = this.encodableType.get(typeName);

    if (this.listenerSubjectMap.has(typeName)) {
      const listenerSubject = this.listenerSubjectMap.get(typeName);
      listenerSubject.complete();
      this.listenerSubjectMap.delete(typeName);
    }
    delete this.callbacks[typeName];
  }

  prepareEvent(event: any, id?: string): any {
    let eventBuf = new CubiqTS.ByteBuffer();
    let messageBuf = new CubiqTS.ByteBuffer();

    if (id === undefined) {
      id = uuid();
    }
    let err = false;
    logger.out.info(
      `${this.constructor.name}.prepareEvent() ${this.sessionId}: prepare event to send:msgid:${id}`,
    );
    try {
      event.encoder.encode(event, eventBuf);
    } catch (ex) {
      logger.out.info(
        "Failed to encode service request " + event.toString() + ". Error:" + ex.toString(),
      );
      err = true;
    }
    if (err) {
      return null;
    }

    let size = eventBuf.offset + id.length + event.encodableType.length;

    eventBuf.flip();
    messageBuf.writeByte(94);
    messageBuf.writeByte(94);
    messageBuf.writeInt(4 + 4 + size);
    messageBuf.writeIString(event.encodableType);
    messageBuf.writeIString(id);

    messageBuf.append(eventBuf);
    messageBuf.flip();

    return {
      messageBuf: messageBuf,
      id: id,
    };
  }

  requestTokenId() {
    logger.out.info(
      `${this.constructor.name}.requestTokenId() ${this.sessionId}: requestTokenId ${this.tokenId}`,
    );

    this.port.postMessage({
      __cubiq_broker_message_type: "requestTokenId",
      __cubiq_broker_message_payload: this.tokenId == null ? null : this.tokenId,
    });
  }

  private requestToken() {
    logger.out.info(
      `${this.constructor.name}.requestToken() ${this.sessionId}: requestToken ${this.tokenId}`,
    );

    this.port.postMessage({
      __cubiq_broker_message_type: "requestToken",
      __cubiq_broker_message_payload: null,
    });
  }

  setToken(serviceResponse) {
    if (serviceResponse == null) {
      this.tokenResponse = null;
      this.token = null;
      this.tokenSubject.next(TokenState.NONE);
      return;
    }
    this.tokenResponse = serviceResponse;
    this.token = serviceResponse.payload;
    this.tokenSubject.next(TokenState.PRESENT);

    let p = this.prepareEvent(serviceResponse);
    let messageBuf = p.messageBuf;
    let id = p.id;
    let payload = messageBuf.toArrayBuffer(false);
    this.tokenId = id;
    this.port.postMessage(
      {
        __cubiq_broker_message_type: "setToken",
        __cubiq_broker_message_payload: payload,
        __cubiq_broker_message_id: id,
        __cubiq_broker_message_timestamp: new Date().valueOf(),
      },
      [payload],
    );
  }

  private setSessionMetadata(metadata: CubiqTS.SessionMetadataImpl) {
    this.connectionSessionId = metadata.connectionSessionId;
    this.proxyConnectionId = metadata.proxyConnectionId;
    logger.out.info(
      `${this.constructor.name} ${this.sessionId}: setSessionMetadata ${this.sessionCtx}`,
    );
    this.port.postMessage({
      __cubiq_broker_message_type: "setSessionMetadata",
      __cubiq_broker_message_payload: {
        connectionSessionId: metadata.connectionSessionId,
        proxyConnectionId: metadata.proxyConnectionId,
      },
    });
  }

  setStaticProfile(staticProfile) {
    this.port.postMessage({
      __cubiq_broker_message_type: "staticProfile",
      __cubiq_broker_message_payload: staticProfile,
    });
  }

  /**
   * Wrapper around standard sendEvent that treats the event as an encodable and
   * logs it as a request before sending it on
   * @param event
   */
  public sendRequestEvent(event: CubiqTS.Encodable) {
    this.cubiqLogger.processRequest(event);
    this.sendEvent(event);
  }

  sendEvent(event: any, useToken = true) {
    if (event.hasOwnProperty("cliTxTime")) {
      event.cliTxTime = Date.now();
    }
    if (useToken && event.secureToken == null) {
      event.secureToken = this.token;
    }
    let p = this.prepareEvent(event);
    let messageBuf = p.messageBuf;
    let id = p.id;
    let payload = messageBuf.toArrayBuffer(false);
    this.port.postMessage(
      {
        __cubiq_broker_message_type: "send",
        __cubiq_broker_message_payload: payload,
      },
      [payload],
    );

    return id;
  }

  sendLoginEvent(event: any): string {
    let tokenId = event.messageId;
    if (event.hasOwnProperty("cliTxTime")) {
      event.cliTxTime = Date.now();
    }
    let p;
    if (this.tokenId != null) {
      tokenId = this.tokenId;
      p = this.prepareEvent(event, this.tokenId);
    } else {
      // must not do `tokenId = p.id;`
      p = this.prepareEvent(event);
    }
    let messageBuf = p.messageBuf;
    let payload = messageBuf.toArrayBuffer(false);
    this.port.postMessage(
      {
        __cubiq_broker_message_type: "login",
        __cubiq_broker_message_payload: payload,
        __cubiq_broker_message_id: tokenId,
      },
      [payload],
    );

    return tokenId;
  }

  disconnect() {
    this.cancelRefreshTokenSubscription();

    this.port.postMessage({
      __cubiq_broker_message_type: "disconnect",
      __cubiq_broker_message_payload: null,
    });
  }

  disconnectOnLogout() {
    this.cancelRefreshTokenSubscription();

    this.port.postMessage({
      __cubiq_broker_message_type: "disconnectOnLogout",
      __cubiq_broker_message_payload: null,
    });

    this.port.removeEventListener("message", this.handler);
    this.port.close();
  }

  connect(location: string) {
    this.serviceLocation = location;

    this.port.postMessage({
      __cubiq_broker_message_type: "connect",
      __cubiq_broker_message_payload: location,
    });
  }

  intFromBytes(x) {
    let val = 0;
    for (let i = 0; i < x.length; ++i) {
      val += x[i];
      if (i < x.length - 1) {
        val = val << 8;
      }
    }
    return val;
  }

  isIdle(): boolean {
    return document?.visibilityState === "hidden";
  }

  processBrokerMessage(ab, id?: string) {
    let buffer = CubiqTS.ByteBuffer.wrap(ab, "utf8", false);
    buffer.readByte();
    buffer.readByte();
    let length = buffer.readInt();

    let cubiqMessage = new CubiqTS.CubiqMessage(length, ab.slice(6));

    let cubiqEvent = new CubiqTS.CubiqEvent();
    cubiqEvent.decode(cubiqMessage);
    let objectTypeName: string = this.resolver.resolveObject(cubiqEvent.getEncodableType());
    // eslint-disable-next-line import/namespace
    let objectInst: CubiqTS.ConcreteEncodable = new CubiqTS[objectTypeName]();
    objectInst.setInnerEncoding(false);
    objectInst.encoder.decode(objectInst, cubiqMessage.getMessage());

    if (typeof id === "string") {
      objectInst["messageId"] = id;
      logger.out.info(`${this.constructor.name}.processBrokerMessage() %o`, objectInst);
      this.isIdle() ? this.dispatchMessage(objectInst) : this.messageBuffer.enqueue(objectInst);
    } else {
      this.isIdle()
        ? this.dispatchBrokerMessage(objectInst)
        : this.brokerBuffer.enqueue(objectInst);
    }

    logger.out.info(
      `${this.constructor.name}.processBrokerMessage() ${this.sessionId}: token decoded ${this.tokenId}`,
    );
  }

  public processSocketMessage(buffer: ArrayBuffer) {
    let currentMessage = new CubiqTS.CubiqMessage(buffer.byteLength, buffer);
    this.processMessage(currentMessage);
  }

  processMessage(cubiqMessage: CubiqTS.CubiqMessage): void {
    let cubiqEvent = new CubiqTS.CubiqEvent();
    cubiqEvent.decode(cubiqMessage);
    let objectTypeName: string = this.resolver.resolveObject(cubiqEvent.getEncodableType());
    // eslint-disable-next-line import/namespace
    let objectInst: CubiqTS.ConcreteEncodable = new CubiqTS[objectTypeName]();
    objectInst.setInnerEncoding(false);
    objectInst.encoder.decode(objectInst, cubiqMessage.getMessage());

    if (objectInst instanceof CubiqTS.SessionMetadataImpl) {
      this.setSessionMetadata(objectInst);
    }

    this.cubiqLogger.processMessage(objectTypeName, objectInst);
    this.isIdle() ? this.dispatchMessage(objectInst) : this.messageBuffer.enqueue(objectInst);
  }

  private dispatchBrokerMessage(message: CubiqTS.ConcreteEncodable) {
    if (message instanceof CubiqTS.ServiceResponseImpl == false) {
      return;
    }

    let serviceResponse = message as CubiqTS.ServiceResponseImpl;

    if (
      CubiqTS.ResponseCode.COMPLETE === serviceResponse.responseCode &&
      serviceResponse.payload != null &&
      serviceResponse.payload instanceof CubiqTS.TrustToken
    ) {
      this.tokenResponse = serviceResponse;
      this.token = serviceResponse.payload;
      this.tokenSubject.next(TokenState.PRESENT);
      logger.out.info(
        `${this.constructor.name}.dispatchBrokerMessage() ${this.sessionId}: token updated ${this.tokenId}`,
      );
    } else {
      logger.out.info(
        `${this.constructor.name}.dispatchBrokerMessage() ${this.sessionId}: no-op ${serviceResponse.messageId}`,
      );
    }
  }

  private dispatchMessage(message: CubiqTS.ConcreteEncodable): void {
    let listeners = this.callbacks[message.encodableType];
    if (listeners != null) {
      for (let i = 0; i < listeners.length; i++) {
        try {
          listeners[i](message);
        } catch (ex) {
          logger.out.info(
            `${this.constructor.name} ${
              this.sessionId
            }: Error upon invoking event listener for event %O object instance %O error: ${ex.toString()} listeners: %O i: ${i}`,
            message.encodableType,
            message,
            listeners,
            ex,
          );
          logger.out.info(ex);
        }
      }
    }
    this.cubiqLogger.postProcessMessage(message, listeners);
  }

  private dispatchBrokerMessages(): void {
    this.brokerBuffer.drainTo((m) => this.dispatchBrokerMessage(m));
  }

  private dispatchMessages(): void {
    this.messageBuffer.drainTo((m) => this.dispatchMessage(m));
  }

  public hasTokenId(): boolean {
    return this.tokenId != null;
  }
  // used by admin directly
  public getToken(): CubiqTS.TrustToken {
    return this.token;
  }
  public getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  private onConnected(): void {
    logger.out.info(`${this.constructor.name}.onConnected() ${this.sessionId} ${this.sessionCtx}`);
    this.connectionStatus.setStatus(ConnectionStatusState.CONNECTED);
    this.connectionStatusSubject.next(this.connectionStatus);

    this.stopReconnectLoop();
    this.stopProcessingLoop();
    this.reconnectionCount = 0;
    this.startProcessingLoop();
  }

  private onReconnected() {
    logger.out.info(
      `${this.constructor.name}.onReconnected() ${this.sessionId} ${this.sessionCtx}`,
    );
    this.connectionStatus.setStatus(ConnectionStatusState.RELOAD);
    this.connectionStatusSubject.next(this.connectionStatus);

    this.stopProcessingLoop();
    this.stopReconnectLoop();

    this.token = null;
    this.tokenId = null;
    this.tokenSubject.next(TokenState.NONE);
  }

  private onDisconnected(): void {
    logger.out.info(
      `${this.constructor.name}.onDisconnected() ${this.sessionId} ${this.sessionCtx}`,
    );

    if (
      this.connectionStatus.getStatus() !== ConnectionStatusState.DISCONNECTED &&
      this.connectionStatus.getStatus() !== ConnectionStatusState.RECONNECTING
    ) {
      this.connectionStatus.setStatus(ConnectionStatusState.DISCONNECTED);
      this.connectionStatusSubject.next(this.connectionStatus);
    }

    if (this.connectionStatus.getStatus() !== ConnectionStatusState.RECONNECTING) {
      this.connectionStatus.setStatus(ConnectionStatusState.RECONNECTING);
      this.connectionStatusSubject.next(this.connectionStatus);
    }

    this.tokenId = null;
    this.token = null;
    this.tokenSubject.next(TokenState.NONE);

    this.scheduleReconnect();
  }

  public proposeRefreshToken(): void {
    if (this.tokenId != null) {
      logger.out.info(
        `${this.constructor.name} ${this.sessionId}: propose refresh token ${this.sessionCtx}`,
      );
      this.port.postMessage({
        __cubiq_broker_message_type: "proposeRefreshToken",
        __cubiq_broker_message_payload: this.tokenId,
      });
    }
  }

  private cancelRefreshTokenSubscription(): void {
    if (this.refreshTokenSubscription != null) {
      this.refreshTokenSubscription.unsubscribe();
      this.refreshTokenSubscription = null;
    }
  }

  public refreshToken(): void {
    // Cancel any previous in-flight refresh before starting a new one
    this.cancelRefreshTokenSubscription();

    let refreshRequest: CubiqTS.SecureTokenRefreshRequestImpl =
      new CubiqTS.SecureTokenRefreshRequestImpl();
    refreshRequest.secureToken = refreshRequest.token = this.token;

    this.refreshTokenSubscription = this.postServiceRequestAsObservable(refreshRequest).subscribe(
      (response) => {
        this.refreshTokenSubscription = null;
        if (response.payload instanceof CubiqTS.TrustToken) {
          this.processTrustToken(response.payload);
        } else {
          this.processTokenFailure(true);
        }
      },
      () => {
        this.refreshTokenSubscription = null;
        this.processTokenFailure(true);
      },
    );
  }

  private processTokenFailure(refreshFailed: boolean): void {
    this.tokenSubject.next(TokenState.NONE);
    if (refreshFailed === true) {
      this.disconnect();
    }
  }

  public getEncodableObservable<T>(typeName: string): Observable<T> {
    const encType = this.encodableType.get(typeName);

    if (!encType) {
      logger.out.error(
        `${this.constructor.name}.getEncodableObservable(): Unknown typeName='${typeName}'. ` +
          `Did you pass a CubiqTS.*.name that exists in resolver/javaToJavaScriptType?`,
      );
      return new Observable<T>((subscriber) => subscriber.complete());
    }

    const subject = this.listenerSubjectMap.get(encType) as Subject<T> | undefined;

    if (!subject) {
      logger.out.error(
        `${this.constructor.name}.getEncodableObservable(): No subject registered for typeName='${typeName}' (encType='${encType}'). ` +
          `Call registerEventChannelListener(${typeName}) first, e.g. ` +
          `broker.registerEventChannelListener(CubiqTS.${typeName}.name);`,
      );
      return new Observable<T>((subscriber) => subscriber.complete());
    }

    return subject.asObservable();
  }

  public processTrustToken(token: CubiqTS.TrustToken) {
    if (this.token != null && token.messageId === this.token.messageId) {
      return;
    }
    if (
      this.token != null &&
      CubiqTS.Long.isLong(this.token.timeStamp) &&
      CubiqTS.Long.isLong(token.timeStamp) &&
      token.timeStamp.lessThanOrEqual(this.token.timeStamp)
    ) {
      logger.out.warn(
        `${this.constructor.name}.processTrustToken() dropping older token timestamp:${token.timeStamp} (kept: ${this.token.timeStamp})`,
      );
      return;
    } else {
      this.tokenResponse.payload = token;
      logger.out.warn(
        `${this.constructor.name}.processTrustToken() accepting token timestamp:${token.timeStamp} (replaced: ${this.token.timeStamp})`,
      );
    }
    this.setToken(this.tokenResponse);
  }

  public postServiceRequest(
    request: CubiqTS.ConcreteEncodable,
    timeoutMs: number = SERVICE_REQUEST_TIMEOUT_MS,
  ): Promise<CubiqTS.Encodable | CubiqTS.Errors> {
    this.cubiqLogger.processRequest(request);
    const start = new Date();
    return new Promise<any>((resolve, reject) => {
      let settled = false;

      const timeoutId = setTimeout(() => {
        if (!settled) {
          settled = true;
          subscription.unsubscribe();
          reject(
            new Error(
              `${this.constructor.name}.postServiceRequest() ${this.sessionId}: timed out after ${timeoutMs} ms ` +
                `waiting for response to ${request.constructor.name} msgId=${request["messageId"]}`,
            ),
          );
        }
      }, timeoutMs);

      let subscription = this.serviceResponseSubject
        .pipe(
          filter((serviceResponse: CubiqTS.ServiceResponseImpl) => {
            return serviceResponse.messageId === request["messageId"];
          }),
        )
        .subscribe(
          (serviceResponse: CubiqTS.ServiceResponseImpl) => {
            let responseCode: any = serviceResponse.responseCode;

            if (CubiqTS.ResponseCode.COMPLETE === responseCode) {
              if (settled) return;
              settled = true;
              clearTimeout(timeoutId);
              subscription.unsubscribe();
              const now = new Date();
              const duration = now.getTime() - start.getTime();
              logger.out.info(
                `${this.constructor.name}.postServiceRequest() ${this.sessionId}: COMPLETE (${duration} ms) `,
                serviceResponse.serviceRequestType,
              );

              resolve(serviceResponse.payload);
            } else if (CubiqTS.ResponseCode.SERVICE_ERROR === responseCode) {
              if (settled) return;
              settled = true;
              clearTimeout(timeoutId);
              subscription.unsubscribe();
              logger.out.error(
                `${this.constructor.name}.postServiceRequest() ${this.sessionId}: SERVICE_ERROR ` +
                  serviceResponse.serviceRequestType,
              );
              if (request["orderIds"] || request["dealIds"])
                serviceResponse.errorList["orderIds"] =
                  request["orderIds"] != undefined ? request["orderIds"] : request["dealIds"];
              resolve(serviceResponse.errorList);
            }
          },
          (error) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            reject(error);
          },
        );
      logger.out.info(
        `${this.constructor.name}.postServiceRequest() ${this.sessionId}: broker.sendEvent: ` +
          request.constructor.name +
          ":time:" +
          Date.now() +
          ":msgId:" +
          request["messageId"],
      );
      this.sendEvent(request);
    });
  }

  public postServiceRequestAsObservable(
    request: CubiqTS.ConcreteEncodable,
    allowNullPayload: boolean = false,
  ): Observable<CubiqTS.ServiceResponseImpl> {
    let subject: Subject<CubiqTS.ServiceResponseImpl> = new Subject();
    this.cubiqLogger.processRequest(request);

    let subscription = this.serviceResponseSubject
      .pipe(
        filter((serviceResponse: CubiqTS.ServiceResponseImpl) => {
          return serviceResponse.messageId === request["messageId"];
        }),
      )
      .subscribe(
        (serviceResponse: CubiqTS.ServiceResponseImpl) => {
          if (serviceResponse != null && serviceResponse.messageId == request["messageId"]) {
            if (allowNullPayload || serviceResponse.payload != null) {
              subject.next(serviceResponse);
            }

            let responseCode: any = serviceResponse.responseCode;

            if (CubiqTS.ResponseCode.COMPLETE === responseCode) {
              subject.complete();
              subscription.unsubscribe();
            } else if (CubiqTS.ResponseCode.PARTIAL_COMPLETE === responseCode) {
              // StandardReport objects yet to arrive
            } else if (
              CubiqTS.ResponseCode.BROKER_ERROR === responseCode ||
              CubiqTS.ResponseCode.INVALID_REQUEST === responseCode ||
              CubiqTS.ResponseCode.SERVICE_ERROR === responseCode
            ) {
              logger.out.error(
                `${this.constructor.name}.postServiceRequestAsObservable() ${this.sessionId}: postServiceRequestAsObservable error: %O`,
                serviceResponse,
              );
              subject.error(
                Error(
                  "postStandardReportServiceRequest flow error: " +
                    CubiqTS.ResponseCode[responseCode],
                ),
              );
              subscription.unsubscribe();
            }
          }
        },
        (error) => {
          logger.out.info(error);
          subject.error(error);
          subscription.unsubscribe();
        },
        () => {
          logger.out.info(
            `${this.constructor.name}.postServiceRequestAsObservable() ${this.sessionId}:  flow completed`,
          );
          subject.complete();
        },
      );
    this.sendEvent(request);
    return subject.asObservable();
  }

  private createStaticDataSubject(
    type: string,
    value: any = null,
    error: Error = null,
  ): ReplaySubject<any> {
    let staticDataSubject;
    if (!this.staticDataSubjectMap.has(type)) {
      staticDataSubject = new ReplaySubject(1);
      this.staticDataSubjectMap.set(type, staticDataSubject);
    } else {
      staticDataSubject = this.staticDataSubjectMap.get(type);
    }

    if (value != null) {
      staticDataSubject.next(value);
    } else if (error != null) {
      staticDataSubject.error(error);
    }

    return staticDataSubject;
  }

  public primeStaticData<T>(type: string): Observable<T> {
    if (!this.fixedKeys.has(type)) {
      this.primeStaticData<CubiqTS.StaticDataProfileImpl>(
        CubiqTS.StaticDataProfileImpl.name,
      ).subscribe((profile) => {
        if (!profile.hasOwnProperty(type)) {
          this.createStaticDataSubject(
            type,
            null,
            new Error("Unsupported static data type:" + type),
          );
          return;
        }
        this.processStaticData(profile, type);
      });
    }
    let staticDataSubject = this.createStaticDataSubject(type);
    return staticDataSubject.pipe(take(1));
  }

  /**
   * @returns true if static data may be primed with current state of broker
   */
  public checkPrime(): boolean {
    let allowPrime =
      ConnectionStatusState.CONNECTED === this.connectionStatus.getStatus() && this.token != null;
    logger.out.info(
      `${this.constructor.name}.checkPrime() ${this.sessionId}: allowPrime:${allowPrime}`,
    );
    return allowPrime;
  }

  public processStaticData(
    profile: CubiqTS.StaticDataProfileImpl | null,
    type: string,
  ): CubiqTS.ErrorsImpl | void {
    switch (type) {
      case CubiqTS.ClientImpl.name: {
        this.createStaticDataSubject(type, profile.clients);
        this.createStaticDataSubject(TRADABLE_CLIENTS, profile.tradableClientCodes);
        break;
      }
      case CubiqTS.AccountImpl.name: {
        this.createStaticDataSubject(type, profile.accounts);
        this.createStaticDataSubject(TRADABLE_ACCOUNTS, profile.tradableAccountCodes);
        break;
      }
      case CubiqTS.CurrencyPairImpl.name:
        this.createStaticDataSubject(type, profile.currencyPairs);
        break;
      case CubiqTS.CurrencyImpl.name:
        this.createStaticDataSubject(type, profile.currencys);
        break;
      case CubiqBrokerClient.name:
        if (profile.user.uiEnabledFg == false) {
          let error = new CubiqTS.ErrorsImpl();
          error.messageId = profile.messageId;
          error.errorMessages = [
            JSON.stringify({
              id: "staticdata.profile.user.uiEnabledFg.false",
            }) as unknown as CubiqTS.Message,
          ];
          return error;
        }
        break;
      case MISC_STATIC_DATA:
        for (let [type, key] of this.miscFixedKeys) {
          if (key != null) {
            this.createStaticDataSubject(type, profile[key]);
          } else {
            this.createStaticDataSubject(type, profile);
          }
        }
        break;
      default: {
        this.createStaticDataSubject(type, profile[type]);
        break;
      }
    }
  }

  public processLogin(
    requests,
    msgIds,
    msgIdtoTypeMap,
  ): Observable<{ messageId: string; type: any; errors?: CubiqTS.ErrorsImpl }> {
    let respCount = 0;
    let processingSubject = new Subject<{
      messageId: string;
      type: any;
      errors?: CubiqTS.ErrorsImpl;
    }>();

    let subscription = this.serviceResponseSubject
      .pipe(
        filter((serviceResponse: CubiqTS.ServiceResponseImpl) => {
          let hasMessageId = msgIds.has(serviceResponse.messageId);
          return hasMessageId;
        }),
      )
      .subscribe(
        (serviceResponse: CubiqTS.ServiceResponseImpl) => {
          if (
            CubiqTS.ResponseCode.INVALID_REQUEST === serviceResponse.responseCode ||
            CubiqTS.ResponseCode.SERVICE_ERROR === serviceResponse.responseCode ||
            CubiqTS.ResponseCode.BROKER_ERROR === serviceResponse.responseCode
          ) {
            subscription.unsubscribe();
            processingSubject.error(Error(`Invalid Service Response Code`));
          } else if (
            serviceResponse.payload instanceof CubiqTS.StaticDataProfileImpl &&
            msgIdtoTypeMap.has(serviceResponse.messageId)
          ) {
            let types = msgIdtoTypeMap.get(serviceResponse.messageId);

            for (let type of types) {
              let errors: CubiqTS.ErrorsImpl | void = this.processStaticData(
                serviceResponse.payload as CubiqTS.StaticDataProfileImpl,
                type,
              );
              processingSubject.next({
                messageId: serviceResponse.messageId,
                type: type,
                ...(errors != null && errors instanceof CubiqTS.ErrorsImpl
                  ? { errors: errors }
                  : undefined),
              });
            }
            let staticDataProfile: CubiqTS.StaticDataProfileImpl =
              serviceResponse.payload as CubiqTS.StaticDataProfileImpl;
            respCount++;
            if (respCount == requests.length) {
              let responseCode: any = serviceResponse.responseCode;
              let complete = CubiqTS.ResponseCode.COMPLETE === responseCode;
              logger.out.info(
                `${this.constructor.name}.processLogin() ${this.sessionId}: complete == ${complete}`,
              );

              if (complete) {
                this._systemName = staticDataProfile.environmentTag;
                subscription.unsubscribe();
                processingSubject.complete();
              } else {
                subscription.unsubscribe();
                processingSubject.error(Error(`Invalid Service Response Code`));
              }
            }

            if (staticDataProfile && staticDataProfile.user) {
              this._user = staticDataProfile.user as CubiqTS.UserImpl;
            }
          } else if (!(serviceResponse.payload instanceof CubiqTS.StaticDataProfileImpl)) {
            let error = Error(
              `${this.constructor.name}.processLogin() ${this.sessionId}: response missing StaticDataProfileImpl payload`,
            );
            logger.out.warn(
              `CubiqWorker: processLogin response %O missing StaticDataProfileImpl payload`,
              serviceResponse,
            );
            subscription.unsubscribe();
            processingSubject.error(error);
          } else if (!msgIdtoTypeMap.has(serviceResponse.messageId)) {
            let error = Error(
              `${this.constructor.name} ${this.sessionId}: response missing ${serviceResponse.messageId} mapping`,
            );
            logger.out.error(
              `${this.constructor.name}.processLogin() ${this.sessionId}: response missing ${serviceResponse.messageId} mapping`,
            );
            subscription.unsubscribe();
            processingSubject.error(error);
          }
        },
        (error) => {
          logger.out.error(
            `${this.constructor.name}.processLogin() ${this.sessionId}: request: %O  error: %O`,
            "staticRequest",
            error,
          );
          subscription.unsubscribe();
          processingSubject.error(error);
        },
        () => {
          logger.out.info(
            `${this.constructor.name}.processLogin() ${this.sessionId}: staticDataProfile flow completed`,
          );
        },
      );

    logger.out.info(`${this.constructor.name}.processLogin() ${this.sessionId}: sendEvent request`);

    requests.forEach((request) => {
      this.cubiqLogger.processRequest(request);
      this.sendEvent(request);
    });

    return processingSubject.asObservable();
  }

  getSystemName(): string {
    return this._systemName;
  }

  getLoggedInUser(): CubiqTS.UserImpl {
    return this._user;
  }

  getUserId(): string {
    return this._user.userId.toString();
  }

  /**
   * Send out an event directly to the specified observable. For Gamma order events, the nested OrderLifeCycleEventImpl
   * instances will not have an objectTypeName attribute as these events are nested inside the gamma event - reason
   * we must explicitly specify the typeName to identify the correct listeners to notify.
   * @param event
   * @param typeName constructor name of CubiqTS ConcreteEncodable subclass
   * @returns true if the event was dispatched correctly.
   */
  public injectEncodable(event: CubiqTS.ConcreteEncodable, typeName: string): boolean {
    if (!event) {
      logger.out.warn("No encodable found to emit - aborting");
      return false;
    }
    if (!typeName) {
      logger.out.warn("No encodable type specified");
      return false;
    }
    let encodableType = this.encodableType.get(typeName);
    const subject = this.listenerSubjectMap.get(encodableType);
    if (subject) {
      try {
        subject.next(event);
      } catch (ex) {
        logger.out.info(
          `Error pushing event with type ${
            event.encodableType
          }  to listener on ${typeName} - error: ${ex.toString()}`,
        );
        return false;
      }
    } else {
      logger.out.warn(`No event subject found for '${typeName}' - aborting`);
      return false;
    }
    return true;
  }

  private scheduleProcessing(delayMs = PROCESS_TICK_MS) {
    if (this.processingTimer != null) return;
    // Add tiny jitter (±2.5 ms for PROCESS_TICK_MS ms)
    const jitter = (Math.random() - 0.5) * (delayMs * JITTER_FRACTION);
    this.processingTimer = setTimeout(() => this.runProcessingTick(), delayMs + jitter);
  }

  private runProcessingTick() {
    const gen = this.processingGeneration;
    this.processingTimer = null;

    // if loop was stopped between scheduling and execution
    if (!this.processingEnabled) return;

    if (this.isProcessing) return; // avoid reentrancy
    this.isProcessing = true;

    const now = performance.now();
    const elapsed = now - this.lastTick;
    this.lastTick = now;

    // Warn if we were delayed far beyond intended cadence (timer clamping / long GC / tab throttling)
    const driftThreshold = PROCESS_TICK_MS * DRIFT_WARN_MULTIPLIER;
    if (elapsed > driftThreshold && now - this.lastDriftWarnAt > DRIFT_WARN_COOLDOWN_MS) {
      this.lastDriftWarnAt = now;
      logger.out.warn(
        `${this.constructor.name}: processing tick drifted: elapsed=${elapsed.toFixed(1)}ms ` +
          `(expected~${PROCESS_TICK_MS}ms, threshold=${driftThreshold}ms) ` +
          `visible=${!this.isIdle()} q=${this.messageBuffer.queued()} bq=${this.brokerBuffer.queued()} gen=${gen}`,
      );
    }

    try {
      this.dispatchBrokerMessages();
      this.dispatchMessages();
    } finally {
      this.isProcessing = false;
    }

    // after main dispatch, when considering post-stop flush
    if (!this.processingEnabled && this.flushAfterStopGen === gen) {
      this.flushAfterStopGen = null;
      try {
        this.dispatchBrokerMessages();
        this.dispatchMessages();
      } catch (err) {
        logger.out.error(
          `${this.constructor.name}.runProcessingTick(): post-stop flush failed`,
          err,
        );
      }
      return;
    }

    // Re-arm only if still enabled AND same generation (no stop/start)
    if (this.processingEnabled && gen === this.processingGeneration) {
      this.scheduleProcessing(PROCESS_TICK_MS);
    }
  }

  private startProcessingLoop() {
    if (this.processingEnabled) return;

    this.flushAfterStopGen = null;

    this.processingEnabled = true;
    this.processingGeneration++;

    this.lastTick = performance.now();
    this.scheduleProcessing(PROCESS_TICK_MS);
  }

  private stopProcessingLoop() {
    if (!this.processingEnabled && this.processingTimer == null) {
      return;
    }

    const stopGen = this.processingGeneration;

    this.processingEnabled = false;
    this.processingGeneration++;

    if (this.processingTimer != null) {
      clearTimeout(this.processingTimer);
      this.processingTimer = null;
    }

    if (this.isProcessing) {
      this.flushAfterStopGen = stopGen;
      return;
    }

    // safe immediate flush
    try {
      this.dispatchBrokerMessages();
      this.dispatchMessages();
    } catch (err) {
      logger.out.error(`${this.constructor.name}.stopProcessingLoop(): flush failed`, err);
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectionTimeoutId != null) return;

    const delay = this.nextReconnectDelay();

    this.reconnectionTimeoutId = setTimeout(() => {
      this.reconnectionTimeoutId = null;
      this.runReconnectOnce();
    }, delay);

    logger.out.info(
      `${this.constructor.name}.scheduleReconnect() ${this.sessionId}: attempt #${this.reconnectionCount + 1} in ${delay} ms`,
    );
  }

  /**
   * "full jitter" strategy
   *   delay = random_between(0, min(cap, base * 2^attempt))
   *
   * de-correlates competing clients while still respecting
   * exponential growth toward the cap.
   */
  private nextReconnectDelay(): number {
    const exponential = Math.min(
      CubiqBrokerClient.MAX_RECONNECT_BACKOFF_MS,
      this._reconnectionDelay * Math.pow(2, this.reconnectionCount),
    );
    return Math.floor(Math.random() * exponential);
  }

  private stopReconnectLoop(): void {
    if (this.reconnectionTimeoutId != null) {
      clearTimeout(this.reconnectionTimeoutId);
      this.reconnectionTimeoutId = null;
    }
    this.reconnectionCount = 0;
  }

  private runReconnectOnce(): void {
    this.reconnectionCount++;
    this.connect(this.serviceLocation);
    this.scheduleReconnect();
  }
}
